import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiGetInvitation, apiAcceptInvitation } from '../api';
import PasswordInput from '../components/PasswordInput';

/**
 * Public page reached from an invitation link (/accept-invitation?token=…).
 * The invitee picks their own username and password; the band access comes
 * from the invitation itself.
 */
export default function AcceptInvitationPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [info, setInfo] = useState<{ email: string; band: string | null; band_role: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError(t('acceptInvitation.missingToken')); setLoading(false); return; }
    let cancelled = false;
    apiGetInvitation(token)
      .then((i) => {
        if (cancelled) return;
        setInfo(i);
        // Suggest a username from the email local part.
        setUsername(i.email.split('@')[0] || '');
      })
      .catch((e) => { if (!cancelled) setError(e?.message || t('acceptInvitation.invalidExpired')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError(t('acceptInvitation.mismatch')); return; }
    setSubmitting(true);
    try {
      await apiAcceptInvitation(token, username, password);
      // The server signs us in straight away.
      window.location.href = '/home';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the account.');
      setSubmitting(false);
    }
  }

  if (loading) return <div className="page-pad">{t('acceptInvitation.loading')}</div>;

  if (error && !info) {
    return (
      <div className="page-pad" style={{ maxWidth: 420, margin: '60px auto' }}>
        <div className="card" style={{ padding: 28, textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{t('acceptInvitation.unavailable')}</h2>
          <p style={{ color: 'var(--text-dim)' }}>{error}</p>
          <button className="btn btn-secondary" onClick={() => navigate('/login')}>{t('acceptInvitation.goToSignIn')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-pad" style={{ maxWidth: 420, margin: '60px auto' }}>
      <div className="card" style={{ padding: 28 }}>
        <h1 style={{ marginTop: 0 }}>{t('acceptInvitation.title')}</h1>
        <p style={{ color: 'var(--text-dim)' }}>
          <Trans i18nKey="acceptInvitation.invitedAs" values={{ email: info?.email }} components={{ strong: <strong /> }} />
          {info?.band && (
            <Trans i18nKey="acceptInvitation.toBand" values={{ band: info.band, role: info.band_role }} components={{ strong: <strong /> }} />
          )}.
        </p>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 18 }}>
          <div>
            <label>{t('acceptInvitation.username')}</label>
            <input className="form-input" value={username} required
              onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div>
            <label>{t('acceptInvitation.password')}</label>
            <PasswordInput className="form-input" value={password} required
              onChange={(e) => setPassword(e.target.value)} />
            <small style={{ color: 'var(--text-dim)' }}>
              {t('acceptInvitation.pwHint')}
            </small>
          </div>
          <div>
            <label>{t('acceptInvitation.confirmPassword')}</label>
            <PasswordInput className="form-input" value={confirm} required
              onChange={(e) => setConfirm(e.target.value)} />
          </div>
          {error && <div style={{ color: 'var(--danger)', fontSize: '0.9rem' }}>{error}</div>}
          <button className="btn btn-primary" type="submit" disabled={submitting}>
            {submitting ? t('acceptInvitation.creating') : t('acceptInvitation.createAccount')}
          </button>
        </form>
      </div>
    </div>
  );
}
